/**
 * SimpleBarChart Component
 *
 * Pure CSS/HTML horizontal bar chart.
 * No external charting library required.
 */

interface BarData {
  label: string;
  value: number;
  color?: string;
  formattedValue?: string;
}

interface SimpleBarChartProps {
  data: BarData[];
  className?: string;
  showValues?: boolean;
  maxLabelWidth?: string;
}

export function SimpleBarChart({
  data,
  className = '',
  showValues = true,
  maxLabelWidth = '120px',
}: SimpleBarChartProps): JSX.Element {
  const maxValue = Math.max(...data.map((d) => d.value), 1);

  return (
    <div className={`space-y-2 ${className}`}>
      {data.map((item, index) => {
        const percentage = (item.value / maxValue) * 100;
        const barColor = item.color || 'bg-evergreen';

        return (
          <div key={index} className="flex items-center gap-3">
            <div
              className="text-sm text-ink truncate flex-shrink-0"
              style={{ width: maxLabelWidth }}
              title={item.label}
            >
              {item.label}
            </div>
            <div className="flex-1 h-6 bg-soft-neutral rounded overflow-hidden relative">
              <div
                className={`h-full ${barColor} transition-all duration-300 rounded`}
                style={{ width: `${Math.max(percentage, 1)}%` }}
              />
            </div>
            {showValues && (
              <div className="text-sm text-muted w-20 text-right flex-shrink-0">
                {item.formattedValue ?? item.value.toLocaleString()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default SimpleBarChart;
